/**
 * The flight scene's half of ENHANCED EFFECTS: the two clouds, the emitter that
 * feeds them, and the per-frame source collection.
 *
 * Split out of `flight-scene.ts` for the reason `scene-propellers.ts`,
 * `scene-killcam.ts` and `scene-flyby-zoom.ts` were — the host sits close to
 * aislop's size ceiling and a cohesive addition belongs in a sibling.
 * `flight-scene.ts` keeps one field, one constructor call and one line in its
 * settings setter; `scene-draw.ts` makes the frame-loop call. The *rules* — how
 * a flame is thrown and what colour it is — are in `particle-effects.ts`,
 * `particles.ts` and `fire-palette.ts`, none of which import `three`.
 *
 * The host is described structurally rather than by importing `SceneState`,
 * which is private to `flight-scene.ts`.
 */

import * as THREE from "three/webgpu";
import type { Pose } from "@red-opus/engine";
import { ParticleCloud } from "../three/particle-cloud.ts";
import { clearsLens, LENS_CLEARANCE_M } from "./effect-sprites.ts";
import type { EffectsSettings } from "./effects-settings.ts";
import type { MutableRgb } from "./fire-palette.ts";
import {
  collectBurningAircraft,
  collectSceneryFires,
  collectTrainPlumes,
  emptyBurningAircraft,
  type BurningAircraft,
} from "./frame-signals.ts";
import { collectCrashFires } from "./crash-sources.ts";
import { collectShipFires } from "./ship-fires.ts";
import {
  drawableAt,
  ParticleEmitter,
  FIRE_DRAW,
  MAX_FIRE_PARTICLES,
  MAX_PARTICLE_SOURCES,
  MAX_SMOKE_PARTICLES,
  SMOKE_DRAW,
  type ParticleFrame,
} from "./particle-effects.ts";
import type { Particle, ParticleSystem } from "./particles.ts";
import type { EncounterRunner } from "./sim-runner.ts";

/** The pair of samplers one cloud is drawn with (`particle-effects.ts`'s `FIRE_DRAW` / `SMOKE_DRAW`). */
interface DrawRules {
  readonly ramp: (t: number, out: MutableRgb) => MutableRgb;
  readonly fade: (particle: Particle) => number;
}

export interface ParticleEffects {
  /** Add this to the scene once; both clouds draw inside it. */
  readonly group: THREE.Group;
  /**
   * Collects this frame's burning aeroplanes into the buffer this object owns.
   * Returns an empty frame when the toggle is off, which is what stops the
   * emitter running at all rather than merely hiding what it made.
   */
  frameFor(runner: EncounterRunner, pose: Pose, enabled: boolean): ParticleFrame;
  /** `camera` must already be placed — every quad is billboarded off it. */
  update(dt: number, camera: THREE.Camera, frame: ParticleFrame): void;
  /**
   * Re-emits the *same instant* through a second lens, for the kill camera's
   * scissored pass. Spelled as its own method rather than `update(0, …)`: the
   * sprite path uses the `dt = 0` convention and it reads as an accident every
   * time somebody meets it, whereas a burning wreck falling out of the sky is
   * precisely what the corner window exists to show and it had better be
   * obvious that this call is what puts the fire in it.
   */
  redraw(camera: THREE.Camera): void;
  /** Draw calls the particles add this frame: one per cloud with anything in it. */
  drawCallCount(): number;
  dispose(): void;
}

/** An empty frame, returned whenever the toggle is off. Module-level so asking costs no allocation. */
const NO_PARTICLES: ParticleFrame = { sources: [], count: 0 };

class ParticleEffectsImpl implements ParticleEffects {
  readonly group = new THREE.Group();
  private readonly emitter = new ParticleEmitter();
  private readonly fireCloud = new ParticleCloud(MAX_FIRE_PARTICLES);
  private readonly smokeCloud = new ParticleCloud(MAX_SMOKE_PARTICLES);
  /** Filled and re-filled every frame by `collectBurningAircraft` and `collectTrainPlumes`; never reallocated. */
  private readonly sources: BurningAircraft[] = Array.from({ length: MAX_PARTICLE_SOURCES }, emptyBurningAircraft);
  /** Reused so a frame's `ParticleFrame` is not an object literal per frame. */
  private readonly frame: { sources: BurningAircraft[]; count: number } = { sources: [], count: 0 };
  /** This frame's lens position, for `LENS_CLEARANCE_M`. Written once per pass, never allocated. */
  private readonly lens = new THREE.Vector3();

  constructor() {
    this.frame.sources = this.sources;
    this.group.add(this.fireCloud.mesh);
    this.group.add(this.smokeCloud.mesh);
  }

  frameFor(runner: EncounterRunner, pose: Pose, enabled: boolean): ParticleFrame {
    if (!enabled) return NO_PARTICLES;
    // Aeroplanes first, then the trains appended into whatever slots are left,
    // then AD-018's burning buildings after those, then AD-014c's crash sites
    // after those — the buffer is the roster ceiling (nine) plus headroom, a
    // mission briefs at most two trains, and `MAX_SCENERY_FIRES` and
    // `MAX_CRASH_SITES` cap the last two lists at six each, so the four cannot
    // overflow it (`particle-tunables.ts` does the arithmetic).
    const aircraft = collectBurningAircraft(runner, pose, this.sources);
    const withTrains = collectTrainPlumes(runner, this.sources, aircraft);
    const withScenery = collectSceneryFires(runner, this.sources, withTrains);
    const withCrashes = collectCrashFires(runner, this.sources, withScenery);
    // I12's escort sloops last of all, and capped by `SHIP_MAX_COUNT` at three
    // — `particle-tunables.ts` does the arithmetic that says the five lists
    // together still fit the buffer.
    this.frame.count = collectShipFires(runner, this.sources, withCrashes);
    return this.frame;
  }

  update(dt: number, camera: THREE.Camera, frame: ParticleFrame): void {
    this.emitter.advance(dt, frame);
    this.emit(camera);
  }

  redraw(camera: THREE.Camera): void {
    this.emit(camera);
  }

  private emit(camera: THREE.Camera): void {
    camera.getWorldPosition(this.lens);
    this.fireCloud.begin(camera);
    this.smokeCloud.begin(camera);
    this.write(this.fireCloud, this.emitter.fire, FIRE_DRAW);
    this.write(this.smokeCloud, this.emitter.smoke, SMOKE_DRAW);
    this.fireCloud.end();
    this.smokeCloud.end();
  }

  /**
   * Writes one system into its cloud, dropping anything that would sit on the
   * lens.
   *
   * The clearance is `LENS_CLEARANCE_M` **plus the particle's own radius**,
   * where the sprite path asks about the anchor alone. That is not a different
   * policy, it is the same policy applied to a thing that has a size: a puff
   * seven metres out with a six-metre radius has the camera *inside* it, and
   * from the cockpit that is a grey screen — the exact failure
   * `effect-sprites.ts` invented the clearance to prevent, arriving by a
   * different route because a particle grows and a billboard does not.
   */
  private write(cloud: ParticleCloud, system: ParticleSystem, rules: DrawRules): void {
    for (let index = 0; index < system.particles.length; index += 1) {
      const draw = drawableAt(system, index, rules.ramp, rules.fade);
      if (!draw) continue;
      if (!clearsLens(draw.x, draw.y, draw.z, this.lens.x, this.lens.y, this.lens.z, LENS_CLEARANCE_M + draw.radiusM)) {
        continue;
      }
      cloud.push(draw.x, draw.y, draw.z, draw.radiusM, draw.tint.r, draw.tint.g, draw.tint.b, draw.opacity);
    }
  }

  drawCallCount(): number {
    return (this.fireCloud.liveCount > 0 ? 1 : 0) + (this.smokeCloud.liveCount > 0 ? 1 : 0);
  }

  dispose(): void {
    this.fireCloud.dispose();
    this.smokeCloud.dispose();
  }
}

/**
 * Builds both clouds and adds them to `scene`.
 *
 * **Always built, whatever the toggle says.** ENHANCED EFFECTS gates what is
 * *fed* to the emitter (`frameFor`), not whether the clouds exist: an empty
 * frame costs two hidden `InstancedMesh`es and zero draw calls, where building
 * them on demand would mean a pipeline compile at the exact moment an aeroplane
 * catches fire — the hitch `flight-scene.ts`'s `compileAsync` call exists to
 * avoid. It attaches itself rather than handing back a group for the caller to
 * add, because `flight-scene.ts` is at aislop's size ceiling and the line it
 * saves is a line that file does not have.
 *
 * `dispose()` is still the caller's to make; `three/dispose-tree.ts` would in
 * fact reach these two meshes, but a pool that frees itself only as a side
 * effect of somebody else's teardown is a pool that leaks the first time it
 * acquires a texture.
 */
export function createParticleEffects(scene: THREE.Scene): ParticleEffects {
  const effects = new ParticleEffectsImpl();
  scene.add(effects.group);
  return effects;
}

/** What `flight-scene.ts`'s own state has to offer for the particles to be wired up. */
export interface ParticleHost {
  readonly runner: EncounterRunner;
  readonly particles: ParticleEffects;
  /** Read live, so the ENHANCED EFFECTS toggle takes effect on the next frame like KILL CAMERA does. */
  readonly effects: EffectsSettings;
}

/**
 * This frame's particle sources — empty whenever the toggle is off.
 *
 * Also the single place the answer to "is ENHANCED EFFECTS on?" is computed for
 * a frame, so the sprite path's `omitAircraft` and the emitter's own gate
 * cannot disagree and draw an aeroplane's fire twice or not at all.
 */
export function particleFrameFor(host: ParticleHost, pose: Pose): ParticleFrame {
  return host.particles.frameFor(host.runner, pose, host.effects.enhancedEffects);
}
